import React, { useState } from "react";
import { SlotInfo } from "react-big-calendar";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import Select from "react-select";

import classNames from "classnames";
import { capitalize } from "lodash";
import invariant from "tiny-invariant";

import { alertActions } from "../../../../features/alert/alertSlice";
import { selectSelectedTournament } from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { selectUser } from "../../../../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import { createGame, createPlaceholder } from "../../../../lib/api";
import { defaultDateTimeFormat } from "../../../../lib/utils";
import { IMasterTournament } from "../../../../types/api";
import { ICalendarEvent } from "../../../../types/shared";

interface ModalProps extends SlotInfo {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    setEvents?: React.Dispatch<React.SetStateAction<Array<ICalendarEvent>>>;
    resources: {
        resourceTitle: string;
        resourceId: number;
    }[];
    gameType?: "placeholder" | "game";
}

const AddGameModal = ({
    show,
    setShow,
    setEvents,
    start,
    end,
    resourceId,
    resources,
    gameType = "placeholder",
}: ModalProps) => {
    const [selectedAgeLevel, setSelectedAgeLevel] = useState<
        string | undefined
    >();
    const [homeTeam, setHomeTeam] = useState<number>(0);
    const [visitorTeam, setVisitorTeam] = useState<number>(0);
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.auth);
    const tournament = useAppSelector(state =>
        selectSelectedTournament(state, "master"),
    );

    try {
        invariant(tournament);
    } catch (error: any) {
        dispatch(
            alertActions.error({
                title: "An error occurred",
                text: "The tournament was not properly defined. This is an internal error, please contact the developer.",
            }),
        );
        return;
    }

    const tournaments = useAppSelector(
        store => store.tournamentmanagement.tournaments,
    );
    const childTournaments = tournaments.filter(
        t => t.parent === tournament?.id,
    );
    const ageLevels = childTournaments?.map(t => t.ageLevel);

    const handleSubmit = async () => {
        const action =
            gameType === "placeholder"
                ? createPlaceholder(
                      tournament.id,
                      defaultDateTimeFormat.format(new Date(start)),
                      defaultDateTimeFormat.format(new Date(end)),
                      selectedAgeLevel as string,
                      tournament.field?.id,
                      resourceId as number,
                  )
                : createGame(
                      {
                          start: defaultDateTimeFormat.format(new Date(start)),
                          end: defaultDateTimeFormat.format(new Date(end)),
                          field: tournament.field.id,
                          home: homeTeam,
                          visitor: visitorTeam,
                          diamond: resourceId as number,
                      },
                      childTournaments.find(
                          t => t.ageLevel === selectedAgeLevel,
                      )?.id as number,
                      user?.id as number,
                  );
        tournament &&
            tournament.field &&
            resourceId &&
            selectedAgeLevel &&
            action
                .then(response => {
                    const { game } = response;
                    dispatch(
                        alertActions.success({
                            title: `${capitalize(gameType)} created successfully`,
                            dismissible: true,
                            scroll: false,
                        }),
                    );
                    setEvents &&
                        setEvents(prev => {
                            const umpires: string[] = [];

                            game.umpires?.map(u =>
                                umpires.push(
                                    `${capitalize(u.location)}: ${u.name}`,
                                ),
                            );
                            return [
                                ...prev,
                                {
                                    id: game.id,
                                    start: new Date(game.date),
                                    end: game.endDate
                                        ? new Date(game.endDate)
                                        : "",
                                    title: umpires.join("<br>"),
                                    resourceId: game.diamond || 0,
                                    ageLevel: game.ageLevel.ageLevel,
                                },
                            ];
                        });
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem creating the placeholdergame.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                })
                .finally(() => setShow(false));
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered>
            <Modal.Header>
                <Modal.Title>
                    Add {gameType} to {tournament?.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row>
                        <Col xs={4}>
                            <b>Date:</b>
                        </Col>
                        <Col>
                            {start.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                            })}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={4}>
                            <b>Time:</b>
                        </Col>
                        <Col>
                            {start.toLocaleTimeString("en-US", {
                                hour: "numeric",
                                hour12: true,
                                minute: "2-digit",
                            })}{" "}
                            -{" "}
                            {end.toLocaleTimeString("en-US", {
                                hour: "numeric",
                                hour12: true,
                                minute: "2-digit",
                            })}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={4}>
                            <b>Field:</b>
                        </Col>
                        <Col>
                            {
                                resources.find(r => r.resourceId === resourceId)
                                    ?.resourceTitle
                            }
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={4}>
                            <b>Select Age Level:</b>
                        </Col>
                        <Col>
                            <Select
                                isMulti={false}
                                isClearable={false}
                                isSearchable={false}
                                options={ageLevels.map(a => {
                                    return {
                                        label: a,
                                        value: a,
                                    };
                                })}
                                onChange={v => setSelectedAgeLevel(v?.value)}
                            />
                        </Col>
                    </Row>
                    {gameType === "game" && selectedAgeLevel && (
                        <Row className={classNames("mt-2")}>
                            <Col xs={4}>
                                <b>Teams:</b>
                            </Col>
                            <Col>
                                <Select
                                    isSearchable={false}
                                    isClearable={false}
                                    isMulti={false}
                                    options={childTournaments
                                        .find(
                                            t =>
                                                t.ageLevel === selectedAgeLevel,
                                        )
                                        ?.teams?.filter(t =>
                                            homeTeam !== 0
                                                ? t.id !== homeTeam
                                                : true,
                                        )
                                        ?.map(t => ({
                                            value: t.id,
                                            label: t.name,
                                        }))}
                                    onChange={t =>
                                        t?.value && setVisitorTeam(t.value)
                                    }
                                />
                                <Select
                                    isSearchable={false}
                                    isClearable={false}
                                    isMulti={false}
                                    options={childTournaments
                                        .find(
                                            t =>
                                                t.ageLevel === selectedAgeLevel,
                                        )
                                        ?.teams?.filter(t =>
                                            visitorTeam !== 0
                                                ? t.id !== visitorTeam
                                                : true,
                                        )
                                        ?.map(t => ({
                                            value: t.id,
                                            label: t.name,
                                        }))}
                                    onChange={t =>
                                        t?.value && setHomeTeam(t.value)
                                    }
                                />
                            </Col>
                        </Row>
                    )}
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShow(false)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={handleSubmit}
                    disabled={
                        gameType === "game" &&
                        (homeTeam === 0 || visitorTeam === 0)
                    }
                >
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddGameModal;
